/**
 * CXM logo, traced from the brand asset shipped in cointrader (client-cxm/cxm,
 * `public/assets/images/logos/company-logo.png`). Coordinates are in the
 * asset's own 3717×1118 space; use `CXM_LOGO_VIEWBOX` on the `<svg>`.
 */
export const CXM_LOGO_VIEWBOX = "0 0 3717 1118";
export const CXM_LOGO_GREEN = "#32ff84";

export interface CxmLogoShape {
  /**
   * Where this shape sits across the mark, 0 at the leftmost edge and 1 at the
   * rightmost. Both lists are sorted by it, which is what makes the loader's
   * even per-shape stagger travel left to right. It is not used as the delay
   * itself: the emblem's shards all sit in the leftmost eighth, so scaling by
   * position would fire them together.
   */
  at: number;
  d: string;
}

/** The four shards of the emblem, left to right. */
export const CXM_EMBLEM_SHAPES: readonly CxmLogoShape[] = [
  {
    at: 0.0,
    d: "M563.3,1016.1C589.7,919.1 592.9,887.0 579.6,856.3C569.0,831.8 284.7,548.9 262.1,540.4C232.3,529.1 191.1,533.5 99.8,557.7C50.8,570.7 9.6,582.4 8.3,583.7C6.9,585.1 124.9,704.3 270.4,848.6C415.9,993.0 535.7,1110.2 536.8,1109.1C537.8,1107.9 549.7,1066.1 563.3,1016.1Z",
  },
  {
    at: 0.0345,
    d: "M531.0,311.9C726.1,259.7 745.2,251.4 773.2,207.3C788.1,183.8 838.4,14.4 832.3,8.3C830.3,6.3 141.9,186.9 120.4,195.2C110.1,199.1 232.0,325.0 261.0,340.3C313.3,368.0 326.6,366.6 531.0,311.9Z",
  },
  {
    at: 0.1302,
    d: "M687.0,557.4C711.5,467.3 730.8,392.8 730.0,392.0C728.6,390.6 423.7,470.3 402.3,477.6C396.6,479.5 435.0,521.9 516.0,603.0C583.4,670.4 639.4,724.6 640.5,723.5C641.7,722.3 662.6,647.6 687.0,557.4Z",
  },
  {
    at: 0.2503,
    d: "M1025.5,642.0C1077.8,449.0 1119.8,290.1 1118.9,289.1C1115.2,284.9 909.9,343.1 896.0,352.2C888.0,357.5 877.4,369.5 872.5,378.9C861.1,401.0 756.0,787.7 756.0,807.7C756.0,816.0 759.0,828.7 762.6,835.9C773.2,856.7 926.8,1009.1 928.7,1000.6C929.6,996.4 973.2,835.1 1025.5,642.0Z",
  },
];

/** The three letters of the wordmark: C, X, M. */
export const CXM_WORDMARK_SHAPES: readonly CxmLogoShape[] = [
  {
    at: 0.5227,
    d: "M2058.0,874.1C2097.5,853.9 2132.7,821.4 2148.7,790.4L2160.3,767.8L2146.7,762.5C2139.1,759.6 2114.4,748.7 2091.7,738.5C2052.8,720.9 2050.0,720.4 2042.4,730.4C2038.0,736.2 2034.3,742.5 2034.2,744.4C2034.1,746.3 2023.5,753.7 2010.7,760.9C1989.0,773.1 1979.4,774.0 1875.1,774.0C1750.2,774.0 1732.8,770.0 1708.4,735.8C1696.1,718.6 1694.8,709.0 1693.1,621.8C1691.0,517.7 1693.8,504.1 1722.8,476.2L1740.8,459.0L1866.0,457.2C2017.1,455.0 2013.4,453.8 2055.3,514.8C2056.0,515.8 2081.3,509.0 2111.6,499.8C2164.4,483.8 2166.4,482.5 2162.9,468.0C2156.2,440.9 2123.4,396.6 2094.1,375.1C2040.5,335.7 2028.6,333.4 1875.0,333.2C1744.0,333.0 1735.2,333.7 1702.2,347.0C1659.9,364.1 1610.5,410.6 1589.8,453.0C1575.9,481.3 1575.0,490.0 1573.1,603.1C1571.3,713.7 1572.2,726.0 1583.9,757.3C1597.8,794.5 1638.1,843.7 1671.7,864.4C1714.7,891.0 1734.8,893.7 1884.0,892.3L2025.0,891.0L2058.0,874.1Z",
  },
  {
    at: 0.731,
    d: "M2443.1,802.5C2488.3,752.2 2533.1,703.4 2542.8,694.1L2560.4,677.3L2658.7,785.6L2756.9,894.0L2839.6,894.0L2922.3,894.0L2796.1,756.0C2726.8,680.1 2670.1,616.0 2670.2,613.5C2670.3,611.0 2725.7,548.3 2793.2,474.2C2860.7,400.1 2916.0,337.3 2916.0,334.7C2916.0,332.1 2881.2,330.0 2838.6,330.0L2761.2,330.0L2672.6,427.5C2623.9,481.1 2578.2,531.2 2571.2,538.7L2558.3,552.4L2457.8,441.2L2357.2,330.0L2276.1,330.0L2194.9,330.0L2209.9,347.7C2218.1,357.5 2274.3,419.8 2334.9,486.1C2395.5,552.4 2445.8,608.9 2446.8,611.6C2447.7,614.3 2391.6,679.0 2322.0,755.3L2195.6,894.0L2278.3,894.0L2361.0,893.9L2443.1,802.5Z",
  },
  {
    at: 1.0,
    d: "M3127.4,732.2L3129.0,570.4L3224.8,732.2L3320.6,894.0L3356.8,893.9L3393.0,893.7L3489.0,730.4L3585.0,567.0L3586.6,730.5L3588.2,894.0L3648.1,894.0L3708.0,894.0L3708.0,612.0L3708.0,330.0L3646.2,330.0L3584.3,330.0L3475.1,514.5C3415.0,616.0 3363.6,702.6 3360.8,706.9C3357.4,712.3 3323.2,659.6 3251.9,538.9C3194.7,442.2 3142.8,355.5 3136.4,346.3C3124.9,329.7 3124.7,329.6 3063.9,331.3L3003.0,333.0L3001.4,613.5L2999.9,894.0L3062.8,894.0L3125.8,894.0L3127.4,732.2Z",
  },
];
